import type { Feedback } from "@engram/core";
import { newId } from "@engram/core/id";
import { ChevronRight, ImagePlus, Mic, Square, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../api";
import { deviceName } from "../auth";
import { db, useQuery } from "../db/client";
import { cachedFile, sha256Hex } from "./attachments";
import { dateFmt, shortDate } from "./format";
import { useLayout } from "./layout";
import { usePrefs } from "./prefs";
import { showToast } from "./toast";
import { useVoiceRecorder, type Recording } from "./voiceRecorder";

/*
 * "Send feedback" (account menu, Settings). Saved as a synced `feedback` row, so it works offline and from the
 * phone; tools/feedback/pull.mjs copies new items into docs/feedback/ and Claude's replies come back here.
 */

const MAX_IMAGE = 15 * 1024 * 1024;
const two = (n: number) => String(n).padStart(2, "0");
const clock = (ms: number) => `${Math.floor(ms / 60000)}:${two(Math.floor(ms / 1000) % 60)}`;

/** What Claude needs to reproduce it, collected automatically. */
function context(layout: string, palette: string): string {
  const path = location.pathname + location.search;
  const noteId = /^\/note\/([^/?#]+)/.exec(location.pathname)?.[1] ?? new URLSearchParams(location.search).get("n") ?? undefined;
  return JSON.stringify({
    path,
    noteId,
    screen: `${innerWidth}×${innerHeight}`,
    dpr: devicePixelRatio,
    layout,
    device: deviceName(),
    build: __APP_BUILD__,
    palette,
    online: navigator.onLine,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: navigator.userAgent,
  });
}

export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const layout = useLayout();
  const prefs = usePrefs();
  const [text, setText] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // Speak: a voice message goes with the feedback and the server types it out afterwards (voiceRecorder.ts).
  const [recording, setRecording] = useState<Recording | null>(null);
  const voice = useVoiceRecorder(setRecording);
  const listening = voice.phase !== "idle";
  // Context is taken when the box opens, so it describes the screen the feedback is about.
  const ctx = useRef(context(layout, prefs.palette));

  useEffect(() => {
    if (!image) return;
    const url = URL.createObjectURL(image);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const pick = (f: File | undefined | null) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return setError("Pick an image (a screenshot or photo).");
    if (f.size > MAX_IMAGE) return setError("That image is over 15 MB.");
    setError(null);
    setImage(f);
  };

  // Saved on this device and closed at once (owner, 16 Oct: "when I hit submit, I want it to look like it's
  // done"); the sync sends it, files first, whenever it can, and the server types out the voice message.
  const send = async () => {
    if (busy) return;
    setBusy(true);
    // Send while still recording = stop and send.
    const voiceMsg = listening ? await voice.stop() : recording;
    if (!text.trim() && !voiceMsg) {
      setBusy(false);
      return;
    }
    let image_sha: string | null = null;
    if (image) {
      image_sha = await sha256Hex(image);
      await db.addFile(image_sha, image);
    }
    let audio_sha: string | null = null;
    if (voiceMsg) {
      audio_sha = await sha256Hex(voiceMsg.blob);
      await db.addFile(audio_sha, voiceMsg.blob);
    }
    const now = Date.now();
    await db.mutate([
      {
        t: "feedback",
        id: newId(),
        set: {
          created: now,
          updated: now,
          text: text.trim(),
          context: ctx.current,
          image_sha,
          image_mime: image?.type ?? null,
          audio_sha,
          audio_mime: voiceMsg?.blob.type || null,
          voice: voiceMsg ? "pending" : "",
          status: "new",
          reply: "",
        },
      },
    ]);
    void db.sync();
    onClose();
    showToast(voiceMsg ? "Feedback sent. Your voice message is typed out in the background." : "Feedback sent.");
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal feedback-dialog"
        onPaste={(e) => pick([...e.clipboardData.files].find((f) => f.type.startsWith("image/")))}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            if (listening) voice.cancel(); // Esc while recording throws the recording away, not the box
            else onClose();
          }
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void send();
        }}
      >
        <h2>Send feedback</h2>
        <textarea
          className="input feedback-text"
          autoFocus
          rows={6}
          maxLength={20000}
          placeholder="What happened, or what would you like? A bug, an idea, something that feels off… Or tap Speak and say it."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {preview && (
          <div className="feedback-image">
            <img src={preview} alt="Attached screenshot" />
            <button className="icon-btn" title="Remove image" onClick={() => setImage(null)}>
              <X size={14} />
            </button>
          </div>
        )}
        {listening ? (
          <div className="voice-bar" role="status">
            <span className={`recorder-dot${voice.phase === "recording" ? " live" : ""}`} />
            <span className="recorder-time">{clock(voice.elapsed)}</span>
            <span className="recorder-level" aria-hidden>
              <span style={{ transform: `scaleX(${voice.phase === "recording" ? Math.min(1, voice.level * 2.2) : 0})` }} />
            </span>
            <span className="voice-label">
              {voice.phase === "starting" ? (
                "Starting…"
              ) : (
                <>
                  Listening…<span className="voice-hint"> Send when you're done</span>
                </>
              )}
            </span>
            <button className="btn small recorder-stop" disabled={voice.phase === "starting"} onClick={() => void voice.stop()} title="Stop and keep it (then type more, or Send)">
              <Square size={11} fill="currentColor" /> Stop
            </button>
            <button className="icon-btn" title="Throw this recording away" aria-label="Cancel recording" onClick={voice.cancel}>
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="feedback-tools">
            {recording ? (
              <span className="voice-chip">
                <Mic size={14} /> Voice message · {clock(recording.ms)}
                <button className="icon-btn" title="Remove the voice message" aria-label="Remove the voice message" onClick={() => setRecording(null)}>
                  <X size={14} />
                </button>
              </span>
            ) : (
              <button className="btn" onClick={() => void voice.start()}>
                <Mic size={15} /> Speak
              </button>
            )}
            {!preview && (
              <button className="btn" onClick={() => fileInput.current?.click()}>
                <ImagePlus size={15} /> Add a screenshot or photo
              </button>
            )}
          </div>
        )}
        <input ref={fileInput} type="file" accept="image/*" hidden onChange={(e) => pick(e.target.files?.[0])} />
        {voice.error && <div className="error-text">{voice.error}</div>}
        {error && <div className="error-text">{error}</div>}
        <p className="small muted">Also sent: this page, screen size, device and app version.</p>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || voice.phase === "starting" || (!text.trim() && !recording && !listening)} onClick={() => void send()}>
            Send
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const STATUS: Record<Feedback["status"], [label: string, title: string]> = {
  new: ["Sent", "Waiting for Claude's next chat"],
  seen: ["Seen", "Claude has read it and is on it"],
  done: ["Done", "Taken care of"],
};

const when = (ms: number) => dateFmt({ day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }).format(ms);

/**
 * Settings > Feedback (owner, 13 Oct: "a summarised view of all feedback… expand to see my original feedback, and the
 * status… and when it was submitted"): one line per item, Claude's summary (the first line until Claude has written
 * one), its status and date; a tap opens the original text, the screenshot, Claude's reply and the dates.
 */
export function FeedbackList() {
  const rows = useQuery<Feedback>("SELECT * FROM feedback WHERE deleted = 0 ORDER BY created DESC LIMIT 200", [], ["feedback"]);
  const [open, setOpen] = useState<string | null>(null);
  // Deleting an item (owner, 14 Oct: "delete this and the previous one") is a tombstone like any other row.
  const remove = async (id: string) => {
    if (!confirm("Delete this feedback?")) return;
    setOpen(null);
    await db.mutate([{ t: "feedback", id, del: true }]);
  };
  if (!rows?.length) return null;
  const done = rows.filter((f) => f.status === "done").length;
  return (
    <>
      <div className="feedback-count muted small">
        {rows.length - done} open · {done} done
      </div>
      <ul className="feedback-list">
        {rows.map((f) => {
          const isOpen = open === f.id;
          const [label, title] = STATUS[f.status] ?? STATUS.new;
          return (
            <li key={f.id} className={isOpen ? "open" : undefined}>
              <button className="feedback-row" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : f.id)}>
                <span className={`feedback-status feedback-${f.status}`} title={title}>
                  {label}
                </span>
                <span className="feedback-summary">
                  {f.audio_sha && <Mic size={13} className="feedback-mic" aria-label="Voice message" />}
                  {f.summary || f.text.split("\n")[0] || VOICE_LINE[f.voice] || "Voice message"}
                </span>
                <span className="feedback-date muted small">{shortDate(f.created)}</span>
                <ChevronRight size={15} className="feedback-chevron muted" />
              </button>
              {isOpen && (
                <div className="feedback-detail">
                  {f.text && <div className="feedback-body">{f.text}</div>}
                  {(f.voice === "pending" || f.voice === "failed") && <div className="muted small">{VOICE_LINE[f.voice]}</div>}
                  {f.audio_sha && <FeedbackFile sha={f.audio_sha} mime={f.audio_mime ?? "audio/webm"} kind="audio" />}
                  {f.image_sha && <FeedbackFile sha={f.image_sha} mime={f.image_mime ?? "image/png"} kind="image" />}
                  {f.reply && (
                    <div className="feedback-reply">
                      <strong>Claude</strong> {f.reply}
                    </div>
                  )}
                  <div className="feedback-foot">
                    <span className="muted small">
                      Sent {when(f.created)}
                      {f.status === "done" && ` · Done ${when(f.updated)}`}
                    </span>
                    <button className="btn link danger-text" onClick={() => void remove(f.id)}>
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

const VOICE_LINE: Record<string, string> = {
  pending: "Voice message, being typed out…",
  failed: "Voice message: couldn't type it out yet. Claude tries again when reading feedback.",
};

/** The screenshot or voice message sent with an item: from this device's caches (sent from here), else the server. */
function FeedbackFile({ sha, mime, kind }: { sha: string; mime: string; kind: "image" | "audio" }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    void (async () => {
      const res = (await cachedFile(sha).catch(() => undefined)) ?? (await apiFetch(`/files/${sha}`).catch(() => null));
      if (!res?.ok || !alive) return;
      made = URL.createObjectURL(new Blob([await res.arrayBuffer()], { type: mime }));
      if (alive) setUrl(made);
      else URL.revokeObjectURL(made);
    })();
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [sha, mime]);
  if (!url) return null;
  if (kind === "audio") return <audio className="feedback-audio" controls src={url} preload="metadata" />;
  return (
    <a className="feedback-image" href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="Screenshot sent with this feedback" />
    </a>
  );
}
